/*
=========================================================
* Material Kit 2 React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Kit 2 React components
import categoryColor from "utils/categoryColor";
import MKBox from "../../../../components/MKBox";
import MKTypography from "../../../../components/MKTypography";

import MyLine from "../../../Charts/myLine";

function AssessmentCounterCard({
  category,
  color,
  count,
  title,
  suffix,
  description,
  decimals,
  chart,
  high,
  bg,
  ...rest
}) {
  let data = [
    {
      name: "2000",
      value: 0,
    },
  ];

  if (chart) {
    const years = ["2000", "2010", "2020"];
    data = years.map((year) => {
      const item = chart.find((a) => a.year === year);
      if (Array.isArray(item.number)) {
        const obj = {
          name: year,
        };
        item.number.forEach((numObj) => {
          obj[numObj.class] = numObj.number;
        });
        return obj;
      }
      return {
        name: year,
        value: item ? item.number : 0, // default to 0 if year not found
      };
    });
  }

  let countContent;

  if (count) {
    if (typeof count === "string") {
      countContent = (
        <MKTypography
          color="text"
          variant={high ? "h2" : "h3"}
          mb={high ? 2 : 0}
          sx={{ height: "50px" }}
        >
          {parseFloat(count).toFixed(decimals)}
          {suffix}
        </MKTypography>
      );
    } else if (Array.isArray(count)) {
      countContent = count.map((c) => (
        <MKBox key={c.class} mb={high ? 2 : 0} mx={1} sx={{ height: "50px" }}>
          <MKTypography color="text" variant={high ? "h2" : "h3"}>
            {c.number}
            {suffix}
          </MKTypography>
          <MKTypography color="text" variant="body1">
            {c.class}
          </MKTypography>
        </MKBox>
      ));
    }
  }

  return (
    <MKBox width={1}>
      <MKBox
        textAlign="center"
        lineHeight={1}
        sx={
          count
            ? {
                p: 2,
                background: bg,
                borderRadius: "12px",
              }
            : {
                p: 2,
                border: 1,
                borderColor: categoryColor(category).state,
                borderRadius: "12px",
              }
        }
      >
        {title && (
          <MKTypography
            title={title}
            textTransform="capitalize"
            variant={high ? "h4" : "h5"}
            color={count ? "text" : "text"}
            sx={{ height: "60px" }}
          >
            {title}
          </MKTypography>
        )}
        {count ? (
          <MKBox mb={1} sx={{ display: "flex", justifyContent: "center" }}>
            {countContent}
          </MKBox>
        ) : null}

        {chart ? (
          <MKBox py={high ? 2 : 0}>
            <MKBox
              sx={{
                height: "200px",
              }}
            >
              {suffix === " %" ? (
                <MyLine data={data} yunit="%" {...rest} />
              ) : (
                <MyLine data={data} {...rest} />
              )}
            </MKBox>
          </MKBox>
        ) : null}
      </MKBox>
      {description && (
        <MKBox
          component="div"
          mb={1}
          title={description}
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: "2",
          }}
        >
          <MKTypography variant={high ? "body1" : "body2"} color={count ? "text" : "text"}>
            {description}
          </MKTypography>
        </MKBox>
      )}
    </MKBox>
  );
}

// Setting default props for the DefaultCounterCard
AssessmentCounterCard.defaultProps = {
  color: "dark",
  description: "",
  title: "",
  decimals: 0,
  category: "water",
  high: false,
  count: null,
  chart: null,
  suffix: " %",
  bg: "",
};

// Typechecking props for the DefaultCounterCard
AssessmentCounterCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  decimals: PropTypes.number,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  chart: PropTypes.oneOfType([PropTypes.array]),
  title: PropTypes.string,
  bg: PropTypes.string,
  category: PropTypes.string,
  high: PropTypes.bool,
  description: PropTypes.string,
  suffix: PropTypes.string,
};

export default AssessmentCounterCard;
